// Cross-tab auth signalling. Tab B (`/verify-email`) posts a message
// on the shared channel the moment the backend confirms the token;
// Tab A (the "check your email" panel in AuthModal) subscribes to the
// same channel and auto-advances so the user isn't left staring at
// stale copy in the original tab.
//
// BroadcastChannel is same-origin, same-browser only. For users who
// open the verify link in a DIFFERENT browser or private-mode tab,
// AuthModal also polls `/api/auth/me` every 3s as a safety net —
// see `AuthModal.jsx`.

const CHANNEL_NAME = 'pnt-auth';

// Cached channel instance so multiple listeners in the same tab
// share one underlying BroadcastChannel (browsers cap per-tab count).
let _channel = null;

const _getChannel = () => {
  if (typeof window === 'undefined' || typeof window.BroadcastChannel !== 'function') {
    return null;
  }
  if (!_channel) {
    try {
      _channel = new BroadcastChannel(CHANNEL_NAME);
    } catch (err) {
      // Old browsers / restrictive sandboxes — silently fall back to
      // the polling path in AuthModal. No console noise to avoid
      // scaring the user.
      _channel = null;
    }
  }
  return _channel;
};

// Fire-and-forget: post an auth event to sibling tabs. Safe to call
// even when BroadcastChannel is unsupported (silently no-ops).
export function broadcastAuthEvent(type, payload = {}) {
  const ch = _getChannel();
  if (!ch) return;
  try {
    ch.postMessage({ type, ...payload, at: Date.now() });
  } catch (err) {
    /* channel closed or serialization failure — nothing we can do */
  }
}

// Subscribe to auth events. Returns an `unsubscribe` function; caller
// MUST invoke it on unmount so the listener doesn't leak. `handler`
// receives the raw `{type, ...payload, at}` object.
export function subscribeAuthEvents(handler) {
  const ch = _getChannel();
  if (!ch || typeof handler !== 'function') return () => {};
  const wrapped = (ev) => {
    if (ev && ev.data && typeof ev.data === 'object') handler(ev.data);
  };
  ch.addEventListener('message', wrapped);
  return () => {
    try { ch.removeEventListener('message', wrapped); } catch (err) { /* noop */ }
  };
}

// Event-type constant so both producer and consumer stay in sync.
export const AUTH_EVENT_EMAIL_VERIFIED = 'email_verified';
